import React, {Component} from 'react'
import propTypes from 'prop-types'
import {get, isEmpty, isNil} from 'lodash'
import Checkbox from '@material-ui/core/Checkbox'
import IconButton from 'material-ui/IconButton'
import PivotTableExporter from '../../containers/pivotTableExporter'
import Header from './components/header'
import ColumnHeader from './components/columnHeader'
import ColumnHeaders from './components/columnHeaders'
import List from './components/list'
import Grid from './components/grid'
import Group from './components/group'
import PageControl from './components/pageControl'
import FieldPicker from './components/fieldPicker'
import Placeholder from '../../components/placeholder'
import Loading from '../../components/loading'
import FileUploader from '../../components/fileUploader'
import Icon from '../../components/icon'
import iconNames from 'theme/icons'
import themeVars from 'theme/themeVars'
import {prefix} from 'helpers/component'
import {addVisibleTotalColumnToGroupData} from 'helpers/pivotTable'

const getClassName = prefix('pivotTable-')
require('./pivotTable.scss')

const styles = {
  checkbox: {
    width: 24,
    height: 24,
  },
}

class PivotTable extends Component {
  constructor(props) {
    super(props)
    this.state = {
      expandedGroups: {},
      expandedSubData: {},
      lastToggledRow: null,
      lastToggledRowChecked: null,
      showColumnMenu: false,
      showExportPrompt: false,
      loaded: false,
    }
  }

  componentDidMount() {
    const {
      onSetVisibility,
      onDeSelectAllRows,
      groups,
    } = this.props

    onSetVisibility(true)

    if (!isNil(onDeSelectAllRows))
      onDeSelectAllRows()

    if (!isEmpty(groups))
      this.getExpandableGroups()
  }

  componentDidUpdate(prevProps) {
    const {
      loading,
      groups,
      data,
    } = this.props

    if (prevProps.loading && !loading)
      this.setState({loaded: true})

    if (groups !== prevProps.groups || data !== prevProps.data)
      this.getExpandableGroups()
  }

  componentWillUnmount() {
    this.props.onSetVisibility(false)
  }

  getExpandableGroups = () => {
    const {
      groups,
      data,
      allColumns,
    } = this.props

    if (isEmpty(groups))
      return

    let previousKey = ''
    const expandedGroups = {}
    for (let dataIndex = 0; dataIndex < data.length; dataIndex++) {

      for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
        const key = groups[groupIndex]
        const value = data[dataIndex]
        const column = allColumns[key]
        const columnGroup = get(column, 'group', null)
        const columnData = get(column, 'data', null)

        const columnValue = !isNil(columnGroup)
          ? columnGroup(value)
          : !isNil(columnData)
            ? columnData(value)
            : ''

        const endKey = `>${key}@${columnValue}`
        previousKey = `${previousKey}` + endKey
        expandedGroups[previousKey] = true
      }

      previousKey = ''
    }

    this.setState({
      expandedGroups,
    })
  }

  onSearch = () => {
    if (this.header) this.header.search()
  }

  showColumnMenu = () => {
    this.setState({showColumnMenu: true})
  }

  hideColumnMenu = () => {
    this.setState({showColumnMenu: false})
  }

  showExportPrompt = () => {
    this.setState({showExportPrompt: true})
  }

  hideExportPrompt = () => {
    this.setState({showExportPrompt: false})
  }

  toggleGroup = (key) => {
    const groups = this.state.expandedGroups
    let cur = groups[key]
    if (cur == null) cur = true
    groups[key] = !cur
    this.setState({expandedGroups: groups})
  }

  expandSubData = (level, key) => {
    const {expandedSubData} = this.state
    const updated = {...expandedSubData}
    updated[level] = key

    // Collapse others at lower levels
    for (const key in Object.keys(updated)) {
      if (key > level) delete updated[key]
    }

    this.setState({expandedSubData: updated}, this.updateActiveDataset)
  }

  collapseSubData = (level) => {
    const {expandedSubData} = this.state
    const updated = {...expandedSubData}

    // Collapse this and others at lower levels
    for (const key in Object.keys(updated)) {
      if (key >= level) delete updated[key]
    }

    this.setState({expandedSubData: updated}, this.updateActiveDataset)
  }

  updateActiveDataset = () => {
    const {onChangeActiveDataset} = this.props
    if (onChangeActiveDataset) onChangeActiveDataset(this.getActiveDataset(), this.getActiveLevel())
  }

  getActiveDataset = () => {
    const {data, showSubData, subDataProp} = this.props
    if (showSubData && subDataProp) return this.getDataset(data, 0, subDataProp, null)
    return null
  }

  getDataset = (data, level, subDataProp, defaultValue) => {
    const expandedIndex = this.state.expandedSubData[level]
    if (!isNil(expandedIndex)) {
      const expandedEntity = data[expandedIndex]
      if (expandedEntity) {
        const subData = get(expandedEntity, subDataProp)
        if (subData) return this.getDataset(subData, level + 1, subDataProp, subData)
      }
    }
    return defaultValue
  }

  getActiveLevel = () => {
    // this is satisfactory because we 'delete' elements from the object when collapsing
    return Object.keys(this.state.expandedSubData).length
  }

  groupData = (data, column) => {
    const ret = {}
    data.forEach(e => {

      const value = column.group != null
        ? column.group(e)
        : column.data(e)

      let group = ret[value]
      if (group == null) {
        group = {value, data: []}
        ret[value] = group
      }

      group.data.push(e)
    })

    return ret
  }

  getData = (
    data,
    mode,
    groupColumns,
    theme,
    controlColumns,
    actionColumns,
    index = 0,
    key = '',
    rowIndex = 0,
    showFixedDataTop = true,
    showFixedDataBottom = true) => {

    const {
      stickyTop,
      stickyBottom,
      highlight,
      allColumns,
      visibleColumns,
      columnSizes,
      rowStyle,
      canReorderItems,
      showHeader,
      onReorderEntity,
      pagingAllowed,
      listEntityClass,
      listEntityClassActions,
      areAllRowsSelected,
      fixedDataTop,
      fixedDataBottom,
      pluralNoun,
      icon,
      fullSize,
      showPlaceholder,
      placeholderText,
      showSubData,
      subDataProp,
      isSearching,
      isFiltering,
      hideFixedRowAddButton,
      placeholderHeightStyle,
      onIsRowSelected,
      onSelectEntity,
      onCanSelect,
      onUpdateVisibleColumns,
      visibleTotalGroupFields,
      onGetShowRowHoverHint,
      onGetRowHoverHintMessage,
    } = this.props

    if (groupColumns != null && groupColumns.length > index) {

      // Group
      const groupColumn = groupColumns[index]
      const groupData = this.groupData(data, groupColumn)
      addVisibleTotalColumnToGroupData(groupData, visibleTotalGroupFields)
      const maxGroups = Object.keys(groupData).length
      let curRowIndex = rowIndex

      // Render fixed data if no groups
      if (index === 0 && maxGroups === 0 && (showFixedDataTop || showFixedDataBottom)) {
        return this.getData(
          [],
          mode,
          null,
          theme,
          controlColumns,
          actionColumns,
          index,
          '',
          0,
          showFixedDataTop,
          showFixedDataBottom)
      }

      return Object.keys(groupData).map((k, i) => {
        const group = groupData[k]
        const selector = (mode !== 'list' || index > 0)
        const groupKey = key + '>' + groupColumn.key + '@' + group.value
        const groupRowIndex = curRowIndex
        const data = get(group, 'data', [])
        const totalGroupData = data.length
        curRowIndex += totalGroupData

        let expanded = this.state.expandedGroups[groupKey]
        if (expanded == null) expanded = true
        return (
          <Group
            key={groupKey}
            theme={theme}
            visibleColumns={visibleColumns}
            visibleTotalGroupFields={visibleTotalGroupFields}
            allColumns={allColumns}
            columnSizes={columnSizes}
            fullSize={fullSize}
            stickyTop={isNil(stickyTop) ? undefined : stickyTop + (showHeader ? 51 : 0)}
            onColumnSize={this.onColumnSize}
            onColumnMove={this.onColumnMove}
            column={groupColumn}
            actionColumns={actionColumns}
            controlColumns={controlColumns}
            group={group}
            count={totalGroupData}
            index={index}
            expanded={expanded}
            showSelector={selector}
            onToggle={() => this.toggleGroup(groupKey)}>
            {this.getData(
              data,
              mode,
              groupColumns,
              theme,
              controlColumns,
              actionColumns,
              index + 1,
              groupKey,
              groupRowIndex,
              showFixedDataTop && i === 0,
              showFixedDataBottom && i === maxGroups - 1)}
          </Group>
        )
      })

    } else if (mode === 'list') {

      // List mode
      return <List
        data={data}
        theme={theme}
        entityClass={listEntityClass}
        entityActions={listEntityClassActions}
        onSelect={onSelectEntity}/>

    } else {

      // Grid mode
      let gridStickyTop, gridStickyBottom
      if (!isNil(stickyTop)) {
        gridStickyTop = 31 + stickyTop + (showHeader ? 51 : 0)
        gridStickyBottom = stickyBottom + (pagingAllowed ? 0 : 0)
      }

      return <>
        <Grid
          data={data}
          startIndex={rowIndex}
          theme={theme}
          highlight={highlight}
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          columnSizes={columnSizes}
          rowStyle={rowStyle}
          canReorderItems={canReorderItems}
          controlColumns={controlColumns}
          actionColumns={actionColumns}
          onRowMove={onReorderEntity}
          onSelect={onSelectEntity}
          onVisibleColumnsChanged={onUpdateVisibleColumns}
          entityActions={listEntityClassActions}
          onCanSelect={onCanSelect}
          areAllRowsSelected={areAllRowsSelected}
          onIsRowSelected={onIsRowSelected}
          fixedDataTop={showFixedDataTop ? fixedDataTop : null}
          fixedDataBottom={showFixedDataBottom ? fixedDataBottom : null}
          stickyTop={gridStickyTop}
          stickyBottom={gridStickyBottom}
          showSubData={showSubData}
          subDataProp={subDataProp}
          expandedSubData={this.state.expandedSubData}
          onExpandSubData={this.expandSubData}
          onCollapseSubData={this.collapseSubData}
          hideFixedRowAddButton={hideFixedRowAddButton}
          onGetShowRowHoverHint={onGetShowRowHoverHint}
          onGetRowHoverHintMessage={onGetRowHoverHintMessage}
        />
        {data.length === 0 && showPlaceholder &&
          <Placeholder
            messageTitle={placeholderText || 'Looks like there\'s no ' + pluralNoun.toLowerCase()}
            messageIcon={icon}
            isSearching={isSearching}
            isFiltering={isFiltering}
            height={placeholderHeightStyle}/>
        }
      </>
    }
  }

  onColumnMove = (fromIndex, toIndex) => {
    const curCols = this.props.visibleColumns
    const newCols = [...curCols]
    newCols.splice(fromIndex, 1)
    newCols.splice(toIndex, 0, curCols[fromIndex])
    this.props.onUpdateVisibleColumns(newCols)
  }

  onColumnSize = (key, size) => {
    const newSizes = {...this.props.columnSizes}
    newSizes[key] = size
    this.props.onUpdateColumnSizes(newSizes)
  }

  onToggleSelectedRow = (e, checked, entity, rowIndex, level) => {
    this.props.onToggleSelectedRow(entity, rowIndex, rowIndex, checked, level)
  }

  isAnyGroupExpanded = () => {
    const {expandedGroups} = this.state
    return Object.values(expandedGroups).some(property => property === true)
  }

  onToggleAllGroups = () => {
    const expandedGroups = {...this.state.expandedGroups}
    const value = !this.isAnyGroupExpanded()

    Object.keys(expandedGroups).forEach(key => {
      expandedGroups[key] = value
    })

    this.setState({
      expandedGroups,
    })
  }

  getControlColumns = () => {
    const {
      groups,
      canReorderItems,
      canSelectRows,
      areAllRowsSelected,
      onIsRowSelected,
      onSelectAllRows,
      onDeSelectAllRows,
      controlColumns,
      columnSelectAllowed,
      onCanSelect,
    } = this.props

    let ret = []
    if (!isEmpty(groups)) {
      ret.push({
        key: 'expand-collapse-column',
        label:
          <IconButton
            className={getClassName(['columnButton', 'expand-collapse'])}
            tooltip={'Expand / Collapse'}
            onClick={this.onToggleAllGroups}>
            <Icon
              name={this.isAnyGroupExpanded()
                ? iconNames.collapse
                : iconNames.expand}
              size={12}/>
          </IconButton>,
        size: 32,
        onGetContent: ({key, style, className}) => {
          return <span key={key} style={style} className={className}/>
        },
      })
    }

    if (columnSelectAllowed) {
      ret.push({
        key: 'field-selector-column',
        label:
          <IconButton
            className={getClassName(['columnButton', 'field-selector'])}
            tooltip={'Fields'}
            onClick={this.showColumnMenu}>
            <Icon name={iconNames.fieldActive} size={16}/>
          </IconButton>,
        size: 32,
        onGetContent: ({key, style, className}) => {
          return <span key={key} style={style} className={className}/>
        },
      })
    }

    if (canReorderItems) {
      ret.push({
        key: 'grab-column',
        label: '',
        size: 24,
        onGetContent: ({key, isFixed, rowProps, style, className}) => {
          let content
          if (!isFixed) {
            const dragHandleProps = rowProps.dragHandleProps || {}
            content = (
              <div
                {...dragHandleProps}
                className={getClassName('grabColumn')}
                title="Change the order"
              >
                <Icon
                  name="grab"
                  color={themeVars.palette.disabledColor}
                  className={getClassName('grabIcon')}
                />
              </div>
            )
          }
          return <span key={key} style={style} className={className}>{content}</span>
        },
      })
    }

    // Handling all rows selection with column header check box
    if (canSelectRows) {
      const toggleSelectedRows = areAllRowsSelected ? onDeSelectAllRows : onSelectAllRows

      ret.push({
        key: 'checkBoxColumn',
        label: (
          <div className={getClassName('checkboxColumnHeader')}>
            <Checkbox
              checked={areAllRowsSelected}
              onChange={toggleSelectedRows}
              style={styles.checkbox}
            />
          </div>
        ),
        size: 50,
        onGetContent: ({key, entity, rowIndex, style, className, isFixed, level}) => {
          let content

          if (!isFixed) {
            const canSelect = onCanSelect ? onCanSelect(entity, rowIndex, level) : true
            const isSelected = onIsRowSelected
              ? onIsRowSelected(entity, rowIndex, level)
              : get(entity, 'selected', false)

            content = (
              <Checkbox
                onChange={canSelect
                  ? (e, checked) => this.onToggleSelectedRow(e, checked, entity, rowIndex, level)
                  : undefined}
                disabled={!canSelect}
                checked={isSelected}
                style={styles.checkbox}
              />
            )
          }

          return (
            <span
              key={key}
              style={style}
              className={`${className} ${getClassName('checkboxColumn')}`}>
                {content}
              </span>
          )
        },
      })
    }

    if (controlColumns)
      ret = [...ret, ...controlColumns]

    return ret
  }

  showLoading = () => {
    const {loading, showReloads} = this.props
    const {loaded} = this.state
    return loaded
      ? loading && showReloads
      : loading
  }

  render() {
    let {headerPanel, groups, mode, totalRecords} = this.props
    const {
      stateKey,
      singularNoun,
      pluralNoun,
      stickyTop,
      children,
      allColumns,
      visibleColumns,
      columnSelectAllowed,
      columnSizes,
      exportNoun,
      showHeader,
      sortingAllowed,
      pagingAllowed,
      pageSize,
      page,
      sortColumn,
      sortDirection,
      data,
      listEntityClass,
      className,
      groupingAllowed,
      columnReorderAllowed,
      columnResizingAllowed,
      searchingAllowed,
      isGroupByEnabled,
      exportingAllowed,
      exportFileName,
      footerLeft,
      footerRight,
      onSearch,
      onSort,
      onPage,
      onRefresh,
      onUpdateVisibleColumns,
      onUpdateGroups,
      actions,
      footer,
      fullSize,
      showAdd,
      addLabel,
      search,
      searchPlaceholder,
      onAdd,
      isAddButtonDisabled,
      onBulkRemove,
      onUpdatePageSize,
      canSelectRows,
      onSelectAllRows,
      onDeSelectAllRows,
      actionColumns,
      showRowSelector,
      allowDragAndDropFiles,
      tightFilters,
    } = this.props

    const {showColumnMenu, showExportPrompt} = this.state
    const theme = this.context.muiTheme.components.pivotTable

    const listModeAllowed = (listEntityClass != null)
    if (!listModeAllowed && mode === 'list') mode = 'grid'

    if (!groupingAllowed) groups = []
    if (totalRecords == null) totalRecords = data.length  // if not specified, then paging is not supported

    const controlColumns = this.getControlColumns()

    const loader = this.showLoading() ? <Loading/> : null

    const groupColumns = groups.map(g => allColumns[g]).filter(g => g != null)
    let content = this.getData(data, mode, groupColumns, theme, controlColumns, actionColumns)

    const columnHeaders = (
      <ColumnHeaders
        theme={theme}
        columns={visibleColumns.map(c => allColumns[c])}
        columnSizes={columnSizes}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        sortingAllowed={sortingAllowed}
        columnReorderAllowed={columnReorderAllowed}
        columnResizingAllowed={columnResizingAllowed}
        fullSize={fullSize}
        stickyTop={isNil(stickyTop) ? undefined : stickyTop + (showHeader ? 51 : 0)}
        controlColumns={controlColumns}
        actionColumns={actionColumns}
        groupOffset={Math.max(groupColumns.length - 1, 0)}
        onSize={this.onColumnSize}
        onMove={this.onColumnMove}
        onSort={onSort}
        canSelectRows={canSelectRows}
        onSelectAllRows={onSelectAllRows}
        onDeSelectAllRows={onDeSelectAllRows}/>
    )

    // Check whether we need to add drag and drop files area
    if (allowDragAndDropFiles) {
      content = (
        <FileUploader onFileUpload={this.props.onFileUpload} folderLabel={this.props.folderLabel}>
          {content}
        </FileUploader>
      )
    }

    if (headerPanel) headerPanel = <div>{headerPanel}</div>

    let pageControl
    if (pagingAllowed) {
      pageControl = (
        <PageControl
          ref={e => {
            this.pageControl = e
          }}
          theme={theme}
          pageSize={pageSize}
          currentPage={page}
          totalRecords={totalRecords}
          onChange={onPage}
          pluralNoun={pluralNoun}
          footerLeft={footerLeft}
          footerRight={footerRight}
          onUpdatePageSize={onUpdatePageSize}
          showRowSelector={showRowSelector}
        />
      )
    }

    let header
    if (showHeader) {
      header = (
        <Header
          ref={e => {
            this.header = e
          }}
          theme={theme}
          singularNoun={singularNoun}
          pluralNoun={pluralNoun}
          stickyTop={stickyTop}
          allColumns={allColumns}
          groups={groups}
          groupingAllowed={groupingAllowed}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortingAllowed={sortingAllowed}
          searchingAllowed={searchingAllowed}
          isGroupByEnabled={isGroupByEnabled}
          fullSize={fullSize}
          onRefresh={onRefresh}
          onSearched={onSearch}
          onSortChanged={onSort}
          onGroupsChanged={onUpdateGroups}
          actions={actions}
          showAdd={showAdd}
          search={search}
          searchPlaceholder={searchPlaceholder}
          addLabel={addLabel}
          onAdd={onAdd}
          isAddButtonDisabled={isAddButtonDisabled}
          onBulkRemove={onBulkRemove}
          onExport={exportingAllowed ? this.showExportPrompt : null}
          tightFilters={tightFilters}
        >
          {children}
        </Header>
      )
    }

    let columnMenu
    if (columnSelectAllowed) {
      columnMenu = (
        showColumnMenu
          ? (
            <FieldPicker
              allColumns={allColumns}
              onClose={this.hideColumnMenu}
              onVisibleColumnsChanged={onUpdateVisibleColumns}
              visible={showColumnMenu}
              visibleColumns={visibleColumns}
            />
          )
          : null
      )
    }

    let exporter
    if (exportingAllowed) {
      exporter = (
        <PivotTableExporter
          active={showExportPrompt}
          stateKey={stateKey}
          allColumns={allColumns}
          defaultColumns={visibleColumns}
          singularNoun={singularNoun}
          pluralNoun={pluralNoun}
          exportNoun={exportNoun}
          fileName={exportFileName}
          totalRecords={totalRecords}
          onClose={this.hideExportPrompt}
        />
      )
    }

    const classes = ['container']
    if (!isNil(stickyTop))
      classes.push('container-sticky')

    if (isNil(headerPanel) && isNil(header))
      classes.push('container-no-header')

    if (isNil(pageControl) && isNil(footer))
      classes.push('container-no-footer')

    if (fullSize)
      classes.push('container-full')

    return (
      <div className={`${getClassName(classes)} ${className}`}>
        {headerPanel}
        {header}
        {columnHeaders}
        <div className={getClassName('body')}>
          {content}
          {loader}
        </div>
        {pageControl}
        {footer}
        {columnMenu}
        {exporter}
      </div>
    )
  }
}

PivotTable.contextTypes = {
  muiTheme: propTypes.object,
}

PivotTable.propTypes = {
  actions: propTypes.array, // arrayOf(Button.propTypes),
  visibleTotalGroupFields: propTypes.array,
  addLabel: propTypes.node,
  allColumns: propTypes.object.isRequired,
  allowDragAndDropFiles: propTypes.bool,
  areAllRowsSelected: propTypes.bool,
  canReorderItems: propTypes.bool,
  canSelectRows: propTypes.bool,
  children: propTypes.any,
  className: propTypes.string,
  columnSelectAllowed: propTypes.bool,
  columnSizes: propTypes.object.isRequired,
  columnReorderAllowed: propTypes.bool,
  columnResizingAllowed: propTypes.bool,
  controlColumns: propTypes.arrayOf(propTypes.shape({
    key: propTypes.any,
    label: ColumnHeader.propTypes.label,
    size: propTypes.number.isRequired,
    onGetContent: propTypes.func,
  })),
  actionColumns: propTypes.arrayOf(propTypes.shape({
    key: propTypes.any,
    label: ColumnHeader.propTypes.label,
    size: propTypes.number.isRequired,
    onGetContent: propTypes.func,
  })),
  fixedDataTop: propTypes.arrayOf(propTypes.object),
  fixedDataBottom: propTypes.arrayOf(propTypes.object),
  folderLabel: propTypes.string,
  data: propTypes.arrayOf(propTypes.object),
  exportedRows: propTypes.number,
  exportFileName: propTypes.string,
  exporting: propTypes.bool,
  exportingAllowed: propTypes.bool,
  exportingRows: propTypes.number,
  exportNoun: propTypes.string,
  footer: propTypes.element,
  footerLeft: propTypes.any,
  footerRight: propTypes.any,
  fullSize: propTypes.bool, // to use contain the pivot table in a fixed or absolute positioned container
  groupingAllowed: propTypes.bool,
  groups: propTypes.arrayOf(propTypes.string),
  headerPanel: propTypes.element,
  hideFixedRowAddButton: propTypes.bool,
  highlight: propTypes.func,
  icon: propTypes.string.isRequired,
  isSearching: propTypes.bool,
  isFiltering: propTypes.bool,
  isAddButtonDisabled: propTypes.bool,
  listEntityClass: propTypes.oneOfType([
    propTypes.func,     // a React class to instantiate
    propTypes.element,  // a React element to clone
  ]),
  listEntityClassActions: propTypes.array,
  loading: propTypes.bool,
  mode: propTypes.oneOf(['list', 'grid']),
  page: propTypes.number.isRequired,
  pagingAllowed: propTypes.bool,
  pageSize: propTypes.number.isRequired,
  placeholderText: propTypes.node,
  pluralNoun: propTypes.string.isRequired,
  rowStyle: propTypes.func,
  search: propTypes.string,
  searchingAllowed: propTypes.bool,
  isGroupByEnabled: propTypes.bool,
  searchPlaceholder: propTypes.string,
  showAdd: propTypes.bool,
  showHeader: propTypes.bool,
  showPlaceholder: propTypes.bool.isRequired,
  showRepeatQuoteButton: propTypes.bool,
  showSubData: propTypes.bool,
  singularNoun: propTypes.string.isRequired,
  sortColumn: propTypes.string,
  sortDirection: propTypes.oneOf(['asc', 'desc']),
  sortingAllowed: propTypes.bool,
  stateKey: propTypes.string.isRequired,
  stickyBottom: propTypes.number, // for footer controls defaults to 0
  stickyTop: propTypes.number,    // if provided, will render a single header row
                                  // which will stick at the given y co-ordinate
  subDataProp: propTypes.string,
  totalRecords: propTypes.number,
  visibleColumns: propTypes.arrayOf(propTypes.string).isRequired,
  placeholderHeightStyle: propTypes.string,
  showReloads: propTypes.bool,
  tightFilters: propTypes.bool,

  onAdd: propTypes.func,
  onBulkRemove: propTypes.func,
  onCanSelect: propTypes.func,  // (entity: object, rowIndex: number, level: number): bool
  onChangeActiveDataset: propTypes.func,  // (dataset: array, level: number)
  onDeSelectAllRows: propTypes.func,
  onExport: propTypes.func,
  onFileUpload: propTypes.func,
  onIsRowSelected: propTypes.func,  // (entity: object, rowIndex: number, level: number)
  onPage: propTypes.func.isRequired,
  onRefresh: propTypes.func,
  onReorderEntity: propTypes.func,
  onSearch: propTypes.func.isRequired,
  onSelectAllRows: propTypes.func,
  onSelectEntity: propTypes.func,
  onSetVisibility: propTypes.func.isRequired,
  onSort: propTypes.func.isRequired,
  onToggleSelectedRow: propTypes.func,  // (entity: object, startRowIndex: number,
                                        // endRowIndex: number, toggled: bool, level: number)
  onUpdateVisibleColumns: propTypes.func.isRequired,
  onUpdateColumnSizes: propTypes.func.isRequired,
  onUpdateGroups: propTypes.func,
  onUpdatePageSize: propTypes.func,
  showRowSelector: propTypes.bool,
  onGetShowRowHoverHint: propTypes.func,
  onGetRowHoverHintMessage: propTypes.func,
}

PivotTable.defaultProps = {
  sortDirection: 'asc',
  mode: 'grid',
  stickyBottom: 109,
  showHeader: true,
  canReorderItems: false,
  pagingAllowed: true,
  groupingAllowed: true,
  sortingAllowed: true,
  showPlaceholder: true,
  columnSelectAllowed: true,
  columnReorderAllowed: true,
  columnResizingAllowed: true,
  exportingAllowed: true,
  searchingAllowed: true,
  isGroupByEnabled: true,
  loading: false,
  data: [],
  groups: [],
  page: 0,
  fullSize: false,
  reducedGaps: false,
  canSelectRows: false,
  areAllRowsSelected: false,
  searchPlaceholder: null,
  placeholderText: null,
  exporting: false,
  showSubData: false,
  isAddButtonDisabled: false,
  columnSizes: {},
  pageSize: 0,
  showReloads: true,
  visibleTotalGroupFields: [],
  allowDragAndDropFiles: false,
  onSetVisibility: () => {
  },
  onUpdateVisibleColumns: () => {
    console.error('onUpdateVisibleColumns Not implemented')
  },
  onUpdateColumnSizes: () => {
    console.error('onUpdateColumnSizes Not implemented')
  },
  onSearch: () => {
    console.error('onSearch Not implemented')
  },
  onSort: () => {
    console.error('onSort Not implemented')
  },
  onPage: () => {
    console.error('onPage Not implemented')
  },
  onUpdatePageSize: () => {
    console.error('onUpdatePageSize Not implemented')
  },
  showRowSelector: true,
}

export default PivotTable
